import { createNotification } from '@/components';
import Link from '@/components/Link/Link';
import IntlMessages from '@/components/utility/intlMessages';
import { useAuth } from '@/contexts/auth';
import { Button, Form, Input, Space } from 'antd';
import { useEffect, useState } from "react";
import ReactPasswordChecklist from 'react-password-checklist';
import { useNavigate } from 'react-router';

export function ResetPasswordForm(props) {
  const { ResetPassword } = useAuth();
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordValid, setPasswordValid] = useState(false);

  const values = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  async function onSubmit(values) {
    setIsSubmitting(true);

    const payload = {
      ...values,
      code: props.code
    }

    await ResetPassword(payload)
      .then(({ message }) => {
        createNotification("success", message);
        navigate("/login")
      })
      .catch((e) => {
        if (e.response) {
          createNotification("error", e.response.data.message);
        } else {
          createNotification("error", "Network error");
        }
      }).finally(() => setIsSubmitting(false));
  }

  const showPasswordList = () => {
    if (password !== confirmPassword) {
      return true;
    }
    if (!password) {
      return false;
    }
    return !!(password && !isPasswordValid);
  };

  return (
    <Form
      layout={"vertical"}
      form={form}
      onFinish={(values) => {
        onSubmit(values);
      }}
    >
      <Form.Item
        name="password"
        label="Password"
        rules={[
          {
            required: true,
            message: 'Password is required',
          },
        ]}
      >
        <Input.Password
          success={isPasswordValid.toString()}
          size="large"
          type="password"
          onChange={(e) => setPassword(e.target.value)}
          placeholder="********"
        />
      </Form.Item>
      {showPasswordList() && (
        <ReactPasswordChecklist
          rules={[
            "minLength",
            "specialChar",
            "number",
            "capital",
            "lowercase",
            "match",
          ]}
          messages={{
            minLength: "At least 8 characters",
            specialChar: "Contains a symbol",
            number: "Contains a number",
            capital: "At least 1 uppercase letter",
            lowercase: "At least 1 lowercase letter",
          }}
          minLength={8}
          value={form.getFieldValue("password") || ""}
          valueAgain={form.getFieldValue("confirm_password") || ""}
          onChange={(isValid) => {
            setPasswordValid(isValid);
          }}
          iconSize={10}
          invalidColor="#E16408"
          validColor="#00ABAB"
        />
      )}
      <Form.Item
        name="confirm_password"
        label="Confirm Password"
        rules={[
          {
            required: true,
            message: 'Confirm password is required',
          },
        ]}
      >
        <Input.Password
          size="large"
          type="password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="********"
        />
      </Form.Item>
      <Space align="start" className="flex justify-between">
        <div className="flex flex-col">
          <IntlMessages id="page.signInCreateAccount" />
          <Link
            to="/register"
            className="!text-primary hover:text-primary cursor-pointer"
          >
            <IntlMessages id="page.signInCreateAccountLink" />
          </Link>
        </div>
        <Form.Item>
          <div className="flex flex-col">
            <Button
              className="bg-primary border-0 text-white !px-8 shadow-none"
              type="primary"
              shape="round"
              size="large"
              loading={isSubmitting}
              disabled={!submittable || !isPasswordValid}
              htmlType="submit"
            >
              <IntlMessages id="page.forgotPasswordCTA" />
            </Button>
          </div>
        </Form.Item>
      </Space>
    </Form>
  )
}
